import { Avatar } from '@zillow/constellation-10';
import React from 'react';

import type { AuthorAvatarProps } from './AuthorPage.types';

/**
 * Displays the author's avatar image or initials.
 *
 * Two v10 Avatar defaults are overridden here to hold the v8 rendering. Both are
 * deliberate v10 design-system decisions, so both are worth a design sign-off —
 * but neither existed in v8, and on a hero author photo the difference reads as
 * a regression rather than a restyle:
 *
 * 1. `avatarRecipe` base paints a 1px inset ring (`boxShadow: inset 0 0 0
 *    {borderWidths.Avatar.Root.default} {colors.Avatar.Root.border}`, resolving
 *    to 1px gray.500). v8's AvatarMixin drew no border at all, and there is no
 *    prop to opt out, so it is cleared here.
 * 2. v10's size scale diverges from v8's above `md` — lg 64→72, xl 96→104 and
 *    xxl 160→144 — and the internal Avatar.Image slot is then inset by the ring
 *    width on each side. Net effect at `xxl` was a 160px photo rendering at
 *    142px. The v8 pixel sizes are pinned back, and the image is returned to
 *    100% now that there is no ring to leave room for.
 */
const AuthorAvatar: React.FC<AuthorAvatarProps> = ({ avatar, name, size }) => {
    if (!avatar) return null;

    return (
        // v8 marginBottom="sm" (16px) → v10 has no spacing props; css marginBottom: 'default' (16px).
        //
        // The image goes through the src/alt props, NOT an <Image> child: v10
        // Avatar only renders its internal Avatar.Image slot when children are
        // empty, and that slot is what carries the circular clip (border-radius
        // + overflow: hidden + object-fit: cover). An <Image> child is returned
        // verbatim instead, which renders an unclipped square.
        <Avatar
            size={size}
            src={avatar}
            alt={name}
            css={{
                marginBottom: 'default',
                boxShadow: 'none',
                // Literal per-size values rather than a lookup keyed on `size`:
                // Panda only emits a rule for what it can read statically, so a
                // runtime map would produce a class with no CSS behind it. Only
                // the sizes this page uses need pinning; xs/sm/md are identical
                // in both versions.
                width:
                    size === 'xxl'
                        ? '160px'
                        : size === 'xl'
                        ? '96px'
                        : undefined,
                height:
                    size === 'xxl'
                        ? '160px'
                        : size === 'xl'
                        ? '96px'
                        : undefined,
                '& img': {
                    inlineSize: '100%',
                    blockSize: '100%',
                },
            }}
        />
    );
};

export default AuthorAvatar;
